import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Alert,
  IconButton,
  useTheme,
  alpha,
  Autocomplete,
  Chip
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  Close as CloseIcon,
  PersonAdd as PersonAddIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ja } from 'date-fns/locale';
import { motion } from 'framer-motion';
import type { DoctorFormData, Gender, DoctorStatus, ShiftPriority } from '../../types/doctor';
import { useAvailableServices } from '../../hooks/useAvailableServices';
import { useAvailableLanguages } from '../../hooks/useAvailableLanguages';
import { servicesToTasks, languagesToAvailableLanguages, tasksToServices, availableLanguagesToLanguages } from '../../constants/doctorOptions';
import { formatPhoneNumberOnInput, validatePhoneNumber } from '../../utils/phoneFormatter';
import { validateGoogleSpreadsheetUrl } from '../../utils/urlValidator';

interface DoctorRegisterModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: DoctorFormData) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
}

/**
 * 医師登録モーダルコンポーネント
 * 
 * 意図：
 * - 新規医師の登録フォーム提供
 * - バリデーション付きの入力欄
 * - 使いやすいUI/UX
 * 
 * 注意点：
 * - メールアドレスはカンマ区切りで複数入力可能
 * - 初期値は契約済みステータス
 * - エラーハンドリング実装済み
 * 
 * 改善提案：
 * - 医師コードの自動生成
 * - 既存データとの重複チェック
 * - 入力補助機能
 */
export const DoctorRegisterModal: React.FC<DoctorRegisterModalProps> = ({
  open,
  onClose,
  onSave,
  isLoading = false,
  error
}) => {
  const theme = useTheme();
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState<DoctorFormData>({
    doctor_code: '',
    first_name: '',
    last_name: '',
    gender: 'male' as Gender,
    status: '契約済み' as DoctorStatus,
    start_date: new Date().toISOString().split('T')[0],
    termination_date: null,
    medical_spreadsheet_url: '',
    contact_emails: '',
    personal_phone: '',
    company_phone: '',
    shift_priority: 'B' as ShiftPriority,
    supported_services: [],
    supported_languages: []
  });
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof DoctorFormData, string>>>({});

  // 対応可能業務・言語のマスターデータを取得
  const { services: availableServices } = useAvailableServices();
  const { languages: availableLanguages } = useAvailableLanguages();

  // モーダルが開いた時にスクロール位置をトップにリセット
  useEffect(() => {
    if (open && dialogContentRef.current) {
      dialogContentRef.current.scrollTop = 0;
    }
  }, [open]);

  // フォームバリデーション
  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof DoctorFormData, string>> = {};

    if (!formData.first_name.trim()) {
      errors.first_name = '姓は必須です';
    }
    if (!formData.last_name.trim()) {
      errors.last_name = '名は必須です';
    }
    // スプレッドシートURLのバリデーション
    const spreadsheetUrlError = validateGoogleSpreadsheetUrl(formData.medical_spreadsheet_url);
    if (spreadsheetUrlError) {
      errors.medical_spreadsheet_url = spreadsheetUrlError;
    }
    if (!formData.contact_emails.trim()) {
      errors.contact_emails = '連絡先メールアドレスは必須です';
    } else {
      // メールアドレスの簡易バリデーション
      const emails = formData.contact_emails.split(',').map(e => e.trim());
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidEmails = emails.filter(email => !emailRegex.test(email));
      if (invalidEmails.length > 0) {
        errors.contact_emails = '有効なメールアドレスを入力してください';
      }
    }
    if (!formData.start_date) {
      errors.start_date = '稼働開始日は必須です';
    }
    if (formData.status === '解約済み' && !formData.termination_date) {
      errors.termination_date = 'ステータスが解約済みの場合、稼働解約日は必須です';
    }
    
    // 電話番号のバリデーション（API側は両方必須）
    if (!formData.personal_phone?.trim()) {
      errors.personal_phone = '個人用携帯番号は必須です';
    } else {
      const personalPhoneError = validatePhoneNumber(formData.personal_phone);
      if (personalPhoneError) {
        errors.personal_phone = personalPhoneError;
      }
    }

    if (!formData.company_phone?.trim()) {
      errors.company_phone = '社用携帯番号は必須です';
    } else {
      const companyPhoneError = validatePhoneNumber(formData.company_phone);
      if (companyPhoneError) {
        errors.company_phone = companyPhoneError;
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // フォーム送信処理
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      await onSave(formData);
      // 成功時はフォームをリセット
      handleReset();
    } catch (error) {
      // エラーハンドリングは親コンポーネントで実施
    }
  };

  // フォームリセット
  const handleReset = () => {
    setFormData({
      doctor_code: '',
      first_name: '',
      last_name: '',
      gender: 'male' as Gender,
      status: '契約済み' as DoctorStatus,
      start_date: new Date().toISOString().split('T')[0],
      termination_date: null,
      medical_spreadsheet_url: '',
      contact_emails: '',
      personal_phone: '',
      company_phone: '',
      shift_priority: 'B' as ShiftPriority,
      supported_services: [],
      supported_languages: []
    });
    setFormErrors({});
  };

  // モーダルを閉じる時の処理
  const handleClose = () => {
    handleReset();
    onClose();
  };

  // 入力値の変更ハンドラー
  const handleChange = (field: keyof DoctorFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // エラーをクリア
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        component: motion.div,
        initial: { opacity: 0, scale: 0.9 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.9 },
        sx: {
          borderRadius: 2,
          boxShadow: theme.shadows[10],
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle
        sx={{
          p: 3,
          backgroundColor: alpha(theme.palette.primary.main, 0.03),
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <PersonAddIcon />
            <Typography variant="h6" fontWeight={600}>
              新規医師登録
            </Typography>
          </Stack>
          <IconButton onClick={handleClose} size="small" disabled={isLoading}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent 
        ref={dialogContentRef}
        sx={{ 
          p: 3, 
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0,0,0,0.1)',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.3)',
            borderRadius: '4px',
          }
        }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* 重要項目を最上段に配置 */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="医師コード"
              value={formData.doctor_code}
              onChange={(e) => handleChange('doctor_code', e.target.value)}
              error={!!formErrors.doctor_code}
              helperText={formErrors.doctor_code || '任意: 未入力の場合は自動採番されます（例: DR0036）'}
              disabled={isLoading}
              autoFocus
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>シフト優先度</InputLabel>
              <Select
                value={formData.shift_priority}
                onChange={(e) => handleChange('shift_priority', e.target.value)}
                label="シフト優先度"
                disabled={isLoading}
              >
                <MenuItem value="A">A (最優先)</MenuItem>
                <MenuItem value="B">B (標準)</MenuItem>
                <MenuItem value="C">C (低優先)</MenuItem>
              </Select>
              <FormHelperText>シフト割り当ての優先順位</FormHelperText>
            </FormControl>
          </Grid>

          {/* 名前情報 */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="姓（苗字）"
              value={formData.first_name}
              onChange={(e) => handleChange('first_name', e.target.value)}
              error={!!formErrors.first_name}
              helperText={formErrors.first_name}
              disabled={isLoading}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="名（名前）"
              value={formData.last_name}
              onChange={(e) => handleChange('last_name', e.target.value)}
              error={!!formErrors.last_name}
              helperText={formErrors.last_name}
              disabled={isLoading}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>性別</InputLabel>
              <Select
                value={formData.gender}
                onChange={(e) => handleChange('gender', e.target.value)}
                label="性別"
                disabled={isLoading}
              >
                <MenuItem value="male">男性</MenuItem>
                <MenuItem value="female">女性</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* 稼働ステータス */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>稼働ステータス</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                label="稼働ステータス"
                disabled={isLoading}
              >
                <MenuItem value="契約済み">契約済み</MenuItem>
                <MenuItem value="解約済み">解約済み</MenuItem>
              </Select>
              <FormHelperText>通常は「契約済み」を選択</FormHelperText>
            </FormControl>
          </Grid>

          {/* 対応可能業務を稼働ステータスの右に配置 */}
          <Grid item xs={12} sm={6}>
            <Autocomplete
              multiple
              options={availableServices}
              getOptionLabel={(option) => option.name}
              value={servicesToTasks(formData.supported_services || [])}
              onChange={(_, newValue) => {
                handleChange('supported_services', tasksToServices(newValue));
              }}
              disabled={isLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="対応可能業務"
                  placeholder="業務を選択"
                  helperText="医師が対応可能な業務を選択してください"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option.name}
                    {...getTagProps({ index })}
                    size="small"
                  />
                ))
              }
            />
          </Grid>

          {/* 対応可能言語 */}
          <Grid item xs={12} sm={6}>
            <Autocomplete
              multiple
              options={availableLanguages}
              getOptionLabel={(option) => option.name}
              value={languagesToAvailableLanguages(formData.supported_languages || [])}
              onChange={(_, newValue) => {
                handleChange('supported_languages', availableLanguagesToLanguages(newValue));
              }}
              disabled={isLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="対応可能言語"
                  placeholder="言語を選択"
                  helperText="医師が対応可能な言語を選択してください"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option.name}
                    {...getTagProps({ index })}
                    size="small"
                  />
                ))
              }
            />
          </Grid>

          {/* 空のグリッドアイテムで右半分を埋める */}
          <Grid item xs={12} sm={6} />

          {/* 医師診療リストスプレッドシートURL */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="医師診療リストスプレッドシートURL"
              value={formData.medical_spreadsheet_url}
              onChange={(e) => handleChange('medical_spreadsheet_url', e.target.value)}
              error={!!formErrors.medical_spreadsheet_url}
              helperText={formErrors.medical_spreadsheet_url || '任意: 例 https://docs.google.com/spreadsheets/d/xxx'}
              disabled={isLoading}
              placeholder="https://docs.google.com/spreadsheets/d/..."
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="連絡用メールアドレス"
              value={formData.contact_emails}
              onChange={(e) => handleChange('contact_emails', e.target.value)}
              error={!!formErrors.contact_emails}
              helperText={formErrors.contact_emails || '複数のメールアドレスはカンマ（,）で区切って入力'}
              disabled={isLoading}
              required
              multiline
              rows={2}
              placeholder="例: doctor@example.com, doctor.private@gmail.com"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="個人用携帯番号"
              value={formData.personal_phone}
              onChange={(e) => {
                const formatted = formatPhoneNumberOnInput(e.target.value, formData.personal_phone);
                handleChange('personal_phone', formatted);
              }}
              error={!!formErrors.personal_phone}
              helperText={formErrors.personal_phone || '例: 090-1234-5678'}
              disabled={isLoading}
              required
              placeholder="090-1234-5678"
              inputProps={{
                maxLength: 13 // xxx-xxxx-xxxx形式で13文字
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="社用携帯番号"
              value={formData.company_phone}
              onChange={(e) => {
                const formatted = formatPhoneNumberOnInput(e.target.value, formData.company_phone);
                handleChange('company_phone', formatted);
              }}
              error={!!formErrors.company_phone}
              helperText={formErrors.company_phone || '例: 080-1234-5678'}
              disabled={isLoading}
              required
              placeholder="080-1234-5678"
              inputProps={{
                maxLength: 13 // xxx-xxxx-xxxx形式で13文字
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
              <DatePicker
                label="稼働開始日"
                value={formData.start_date ? new Date(formData.start_date) : null}
                onChange={(date) => handleChange('start_date', date ? date.toISOString().split('T')[0] : '')}
                disabled={isLoading}
                format="yyyy/MM/dd"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!formErrors.start_date,
                    helperText: formErrors.start_date,
                    required: true
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
              <DatePicker
                label="稼働解約日"
                value={formData.termination_date ? new Date(formData.termination_date) : null}
                onChange={(date) => handleChange('termination_date', date ? date.toISOString().split('T')[0] : null)}
                disabled={isLoading || formData.status === '契約済み'}
                format="yyyy/MM/dd"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!formErrors.termination_date,
                    helperText: formErrors.termination_date || (formData.status === '契約済み' ? '契約済みの場合は入力不要' : '')
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button onClick={handleClose} disabled={isLoading} color="inherit">
          キャンセル
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {isLoading ? '登録中...' : '登録する'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};